import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { 
  User, 
  CreditCard, 
  Settings, 
  Crown, 
  Calendar, 
  Mail, 
  Phone,
  MapPin,
  Edit3,
  Save,
  X,
  Clock,
  CheckCircle,
  AlertCircle,
  Euro,
  History
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AccountDashboard = ({ user, token, onUserUpdate, onBack }) => {
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const targetLevels = [
    { value: "lycee_general", label: "Lycée général" },
    { value: "lycee_professionnel", label: "Lycée Professionnel" },
    { value: "formation_distance", label: "Formation à distance" },
    { value: "bts", label: "BTS" },
    { value: "but", label: "BUT" },
    { value: "cpes", label: "CPES" },
    { value: "cpge", label: "CPGE" },
    { value: "deust", label: "DEUST" },
    { value: "dn_made", label: "DN MADE" },
    { value: "licence_1", label: "Licence 1" },
    { value: "licence_2", label: "Licence 2" },
    { value: "licence_pro", label: "Licence Pro." },
    { value: "licence_cupge", label: "Licence CUPGE" },
    { value: "master_1", label: "Master 1" },
    { value: "master_2", label: "Master 2" },
    { value: "ecole_specialisee", label: "École Spécialisée" },
    { value: "cmi", label: "CMI" },
    { value: "doctorat", label: "Doctorat" }
  ];

  const levels = [
    { value: "collège", label: "Collège (6ème-3ème)" },
    { value: "lycée", label: "Lycée (2nde-Terminale)" },
    { value: "université", label: "Université (L1-M2)" },
    { value: "recherche", label: "Recherche (Doctorat+)" }
  ];

  useEffect(() => {
    if (user) {
      setEditForm({
        full_name: user.full_name || "",
        email: user.email || "",
        age: user.age || "",
        current_level: user.current_level || "lycée",
        target_level: user.target_level || "licence_1",
        goal_note: user.goal_note || "",
        password: "",
        confirmPassword: ""
      });
    }
    loadPaymentHistory();
    loadSubscriptionInfo();
  }, [user]);

  const loadPaymentHistory = async () => {
    try {
      const response = await axios.get(`${API}/user/payment-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(response.data.transactions || []);
    } catch (error) {
      console.log("Error loading payment history:", error);
      setPaymentHistory([]);
    }
  };

  const loadSubscriptionInfo = async () => {
    try {
      const response = await axios.get(`${API}/user/subscription-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.log("Error loading subscription info:", error);
      setSubscriptionInfo(null);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError("");
    setMessage("");

    try {
      // Valider le mot de passe si fourni
      if (editForm.password || editForm.confirmPassword) {
        if (editForm.password !== editForm.confirmPassword) {
          setError("❌ Les mots de passe ne correspondent pas");
          setLoading(false);
          return;
        }
        if (editForm.password.length < 6) {
          setError("❌ Le mot de passe doit contenir au moins 6 caractères");
          setLoading(false);
          return;
        }
      }

      // Préparer les données à envoyer
      const dataToSend = {
        full_name: editForm.full_name,
        email: editForm.email,
        age: editForm.age,
        current_level: editForm.current_level,
        target_level: editForm.target_level,
        goal_note: editForm.goal_note
      };

      // Ajouter le mot de passe seulement s'il est fourni
      if (editForm.password) {
        dataToSend.password = editForm.password;
      }

      const response = await axios.put(`${API}/auth/profile`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage("✅ Profil mis à jour avec succès !");
      setIsEditing(false);
      
      // Réinitialiser les champs de mot de passe
      setEditForm({
        ...editForm,
        password: "",
        confirmPassword: ""
      });
      
      // Mettre à jour les données utilisateur dans le composant parent
      if (onUserUpdate) {
        onUserUpdate(response.data);
      }

    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Erreur lors de la mise à jour";
      setError(`❌ ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Jamais";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getPremiumStatus = () => {
    if (!user.premium_expires_at) {
      return { status: "Gratuit", color: "bg-gray-500", expires: null };
    }

    const now = new Date();
    const expiration = new Date(user.premium_expires_at);
    
    if (expiration > now) {
      const daysLeft = Math.ceil((expiration - now) / (1000 * 60 * 60 * 24));
      return { 
        status: "Premium", 
        color: "bg-green-500", 
        expires: expiration,
        daysLeft: daysLeft
      };
    } else {
      return { status: "Expiré", color: "bg-red-500", expires: expiration };
    }
  };

  const premiumStatus = getPremiumStatus();

  const tabs = [
    { id: "profile", label: "Mon Profil", icon: <User className="w-4 h-4" /> },
    { id: "subscription", label: "Abonnement", icon: <Crown className="w-4 h-4" /> },
    { id: "payments", label: "Paiements", icon: <CreditCard className="w-4 h-4" /> },
    { id: "settings", label: "Réglages", icon: <Settings className="w-4 h-4" /> }
  ];

  return (
    <div className="min-h-screen bg-black text-green-100 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <Button
            onClick={onBack}
            variant="ghost"
            className="text-green-400 hover:text-green-300"
          >
            <X className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <h1 className="text-3xl font-bold text-green-400">Mon Compte</h1>
        </div>
        
        <div className="flex items-center space-x-4">
          <Badge className={`${premiumStatus.color} text-white px-3 py-1`}>
            {premiumStatus.status === "Premium" && <Crown className="w-3 h-3 mr-1" />}
            {premiumStatus.status}
          </Badge>
          <span className="text-green-300 text-sm">
            {user.free_credits} crédits gratuits
          </span>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className="bg-green-950/20 border border-green-800/30 rounded-lg p-3 mb-6">
          <p className="text-green-400 text-sm">{message}</p>
        </div>
      )}
      
      {error && (
        <div className="bg-red-950/20 border border-red-800/30 rounded-lg p-3 mb-6">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="flex space-x-2 mb-8">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? "default" : "ghost"}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 ${
              activeTab === tab.id
                ? 'bg-green-600 text-black'
                : 'text-green-300 hover:text-green-200'
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </Button>
        ))}
      </div>

      {/* Content based on active tab */}
      <div className="max-w-4xl">
        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="space-y-6">
            <Card className="bg-gray-900/50 border-green-800/30">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-green-400">Informations Personnelles</CardTitle>
                {!isEditing && (
                  <Button
                    onClick={() => setIsEditing(true)}
                    variant="ghost"
                    size="sm"
                    className="text-green-300 hover:text-green-200"
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-green-300 text-sm">Nom complet</label>
                        <Input
                          value={editForm.full_name}
                          onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                          className="bg-black/60 border-green-700/50 text-green-100"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-green-300 text-sm">Email</label>
                        <Input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                          className="bg-black/60 border-green-700/50 text-green-100"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-green-300 text-sm">Âge</label>
                      <Input
                        type="number"
                        min="10"
                        max="99"
                        value={editForm.age}
                        onChange={(e) => setEditForm({...editForm, age: e.target.value})}
                        className="bg-black/60 border-green-700/50 text-green-100"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-green-300 text-sm">Niveau actuel</label>
                        <Select
                          value={editForm.current_level}
                          onValueChange={(value) => setEditForm({...editForm, current_level: value})}
                        >
                          <SelectTrigger className="bg-black/60 border-green-700/50 text-green-100">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-black border-green-700">
                            {levels.map(level => (
                              <SelectItem key={level.value} value={level.value} className="text-green-100">
                                {level.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-green-300 text-sm">Formation visée</label>
                        <Select
                          value={editForm.target_level}
                          onValueChange={(value) => setEditForm({...editForm, target_level: value})}
                        >
                          <SelectTrigger className="bg-black/60 border-green-700/50 text-green-100">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-black border-green-700 max-h-60 overflow-y-auto">
                            {targetLevels.map(level => (
                              <SelectItem key={level.value} value={level.value} className="text-green-100">
                                {level.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-green-300 text-sm">But recherché (optionnel)</label>
                      <Input
                        value={editForm.goal_note}
                        onChange={(e) => setEditForm({...editForm, goal_note: e.target.value})}
                        placeholder="Ex: Intégrer une école d'ingénieur, réussir le bac..."
                        className="bg-black/60 border-green-700/50 text-green-100"
                      />
                    </div>

                    <div className="bg-blue-950/20 border border-blue-800/30 rounded-lg p-4 space-y-3">
                      <h4 className="text-blue-400 font-medium text-sm">🔒 Modifier le mot de passe (optionnel)</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-green-300 text-sm">Nouveau mot de passe</label>
                          <Input
                            type="password"
                            value={editForm.password}
                            onChange={(e) => setEditForm({...editForm, password: e.target.value})}
                            placeholder="Laisser vide pour ne pas changer"
                            className="bg-black/60 border-green-700/50 text-green-100"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-green-300 text-sm">Confirmer le mot de passe</label>
                          <Input
                            type="password"
                            value={editForm.confirmPassword}
                            onChange={(e) => setEditForm({...editForm, confirmPassword: e.target.value})}
                            placeholder="Confirmer le nouveau mot de passe"
                            className="bg-black/60 border-green-700/50 text-green-100"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <Button
                        onClick={handleSaveProfile}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-500 text-black"
                      >
                        <Save className="w-4 h-4 mr-1" />
                        {loading ? "Sauvegarde..." : "Sauvegarder"}
                      </Button>
                      <Button
                        onClick={() => setIsEditing(false)}
                        variant="ghost"
                        className="text-gray-300 hover:text-gray-200"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Annuler
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-green-400" />
                        <span className="text-green-300">Email :</span>
                        <span className="text-green-100">{user.email}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-green-400" />
                        <span className="text-green-300">Nom :</span>
                        <span className="text-green-100">{user.full_name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-green-400" />
                        <span className="text-green-300">Âge :</span>
                        <span className="text-green-100">{user.age} ans</span>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Settings className="w-4 h-4 text-green-400" />
                        <span className="text-green-300">Niveau actuel :</span>
                        <span className="text-green-100">
                          {levels.find(l => l.value === user.current_level)?.label || user.current_level}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span className="text-green-300">Formation visée :</span>
                        <span className="text-green-100">
                          {targetLevels.find(l => l.value === user.target_level)?.label || user.target_level}
                        </span>
                      </div>
                      {user.goal_note && (
                        <div className="flex items-start space-x-2">
                          <MapPin className="w-4 h-4 text-green-400 mt-0.5" />
                          <span className="text-green-300">But recherché :</span>
                          <span className="text-green-100 flex-1">{user.goal_note}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-green-400" />
                        <span className="text-green-300">Membre depuis :</span>
                        <span className="text-green-100">{formatDate(user.created_at)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === "subscription" && (
          <div className="space-y-6">
            <Card className="bg-gray-900/50 border-green-800/30">
              <CardHeader>
                <CardTitle className="text-green-400">Type de Compte</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-black/40 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-full ${premiumStatus.color}`}>
                      {premiumStatus.status === "Premium" ? <Crown className="w-6 h-6 text-white" /> : <User className="w-6 h-6 text-white" />}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-green-100">
                        Compte {premiumStatus.status}
                      </h3>
                      <p className="text-green-300">
                        {premiumStatus.status === "Premium" && premiumStatus.daysLeft > 0
                          ? `Expire dans ${premiumStatus.daysLeft} jour${premiumStatus.daysLeft > 1 ? 's' : ''} (${formatDate(premiumStatus.expires)})`
                          : premiumStatus.status === "Premium"
                            ? `Expiré le ${formatDate(premiumStatus.expires)}`
                            : "3 résolutions gratuites, puis accès payant"
                        }
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-400">{user.free_credits}</p>
                    <p className="text-green-300 text-sm">crédits gratuits</p>
                  </div>
                </div>

                {/* Afficher les informations du forfait actif */}
                {subscriptionInfo?.current_plan && (
                  <div className="mt-4 p-4 bg-green-950/20 border border-green-800/30 rounded-lg">
                    <h4 className="text-green-400 font-medium mb-3 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Forfait Actuel
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-green-300 text-sm">Formule</p>
                        <p className="text-green-100 font-medium">{subscriptionInfo.current_plan.package_name}</p>
                      </div>
                      <div>
                        <p className="text-green-300 text-sm">Montant payé</p>
                        <p className="text-green-100 font-medium">
                          {subscriptionInfo.current_plan.amount} {subscriptionInfo.current_plan.currency.toUpperCase()}
                        </p>
                      </div>
                      <div>
                        <p className="text-green-300 text-sm">Date d'achat</p>
                        <p className="text-green-100 font-medium">
                          {new Date(subscriptionInfo.current_plan.purchased_at).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                      <div>
                        <p className="text-green-300 text-sm">Expiration</p>
                        <p className="text-green-100 font-medium">
                          {formatDate(subscriptionInfo.premium_expires_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {premiumStatus.status !== "Premium" && (
                  <div className="mt-4 p-4 bg-blue-950/20 border border-blue-800/30 rounded-lg">
                    <h4 className="text-blue-400 font-medium mb-2">💡 Passez au Premium</h4>
                    <p className="text-blue-300 text-sm mb-3">
                      Accédez à des résolutions illimitées avec nos forfaits adaptés à votre niveau d'étude.
                    </p>
                    <Button
                      onClick={onBack}
                      className="bg-blue-600 hover:bg-blue-500 text-white"
                    >
                      <Crown className="w-4 h-4 mr-1" />
                      Voir les Forfaits
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === "payments" && (
          <div className="space-y-6">
            <Card className="bg-gray-900/50 border-green-800/30">
              <CardHeader>
                <CardTitle className="text-green-400">Modalités de Paiement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <CreditCard className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl text-green-300 mb-2">Paiements Sécurisés</h3>
                  <p className="text-green-400 mb-4">
                    Tous les paiements sont traités de manière sécurisée via Stripe.
                  </p>
                  <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                    <div className="bg-black/40 p-3 rounded-lg">
                      <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
                      <p className="text-green-300 text-sm">Cartes de crédit</p>
                    </div>
                    <div className="bg-black/40 p-3 rounded-lg">
                      <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
                      <p className="text-green-300 text-sm">PayPal</p>
                    </div>
                  </div>
                </div>

                {paymentHistory.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-green-400 font-medium mb-3">Historique des Paiements</h4>
                    <div className="space-y-2">
                      {paymentHistory.map((transaction, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-black/40 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <Euro className="w-4 h-4 text-green-400" />
                            <div>
                              <p className="text-green-100 text-sm">{transaction.package_name}</p>
                              <p className="text-green-400 text-xs">{formatDate(transaction.date)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-green-100 font-medium">{transaction.amount}€</p>
                            <Badge className="bg-green-600 text-white text-xs">
                              {transaction.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="space-y-6">
            <Card className="bg-gray-900/50 border-green-800/30">
              <CardHeader>
                <CardTitle className="text-green-400">Réglages du Compte</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-black/40 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-green-400" />
                      <div>
                        <p className="text-green-100">Notifications par email</p>
                        <p className="text-green-400 text-sm">Recevoir des mises à jour et offres</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="border-green-700 text-green-300">
                      Activées
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-black/40 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Settings className="w-5 h-5 text-green-400" />
                      <div>
                        <p className="text-green-100">Langue de l'interface</p>
                        <p className="text-green-400 text-sm">Français</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="border-green-700 text-green-300">
                      Français
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-black/40 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <div>
                        <p className="text-red-100">Supprimer le compte</p>
                        <p className="text-red-400 text-sm">Action irréversible</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="border-red-700 text-red-300 hover:bg-red-950">
                      Supprimer
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};